/**
 * @file Task assignment picker helpers for modal forms.
 */

import { state, appData } from "../../app/state/state.js";
import {
    findItem,
    getCollectionName,
    getItemType,
} from "../utils/itemLookup.js";
import {
    getAssignableUsersWithPermission,
    getSortedUsers,
    getUserFullDisplayName,
} from "../utils/userDisplay.js";
import {
    sanitizeHTML,
} from "../utils/contentUtils.js";
import { openConfirmationModal } from "../ui/confirmationModal.js";
import {
    createUserSelectPreset,
} from "../ui/controls/controlPresets.js";
import {
    createSelectControl,
} from "../ui/controls/selectControl.js";
import {
    destroyRegisteredControlsIn,
} from "../ui/controls/controlCore.js";

let getFormItemData = () => ({});

const PEOPLE_PICKER_SEARCH_THRESHOLD = 7;

/**
 * Binds the active form data getter used by compatibility callers.
 * @param {Function} getter - Returns the current mutable form item data.
 */
export function bindLinkedEntityPickerState(getter) {
  getFormItemData = typeof getter === "function" ? getter : () => ({});
}

function currentFormItemData() {
  return getFormItemData() || {};
}

/**
 * Updates the Owner and Assigned Users UI in the Task form based on link context.
 * @param {string | null} collectionName - Parent collection permission scope.
 */
export function updateTaskUserSelectors(collectionName) {
  const formItemData = currentFormItemData();
  const allowedUsers = getAssignableUsersWithPermission(collectionName);
  const ownerRoot = document.querySelector('[data-task-people-control="owner"]');
  if (ownerRoot) {
    destroyRegisteredControlsIn(ownerRoot);
    createUserSelectPreset({
      root: ownerRoot,
      appData,
      name: "owner",
      collectionName,
      value: formItemData.owner || state.currentUser?.uid || "",
      clearable: false,
      searchable: true,
      searchThreshold: PEOPLE_PICKER_SEARCH_THRESHOLD,
      onChange: (value) => {
        formItemData.owner = value || "";
      },
    });
  }
  renderTaskAssignedUsersField(collectionName, getSortedUsers(allowedUsers));
}

function buildTaskAssignedUserOptions({
  allowedUsers,
  allUsersMap,
  selectedValues,
  visibleTeams,
}) {
  const allowedUserIds = new Set(allowedUsers.map((user) => String(user.id)));
  const optionsById = new Map();

  allowedUsers.forEach((user) => {
    optionsById.set(String(user.id), {
      value: String(user.id),
      label: getUserFullDisplayName(user),
      inactive: user?.isActive === false,
      group: "Users",
      order: user.id === state.currentUser?.uid ? 0 : 9999,
      source: user,
    });
  });

  selectedValues.forEach((userId) => {
    const value = String(userId);
    if (optionsById.has(value)) {return;}
    const user = allUsersMap.get(value) || { id: value, name: value };
    optionsById.set(value, {
      value,
      label: getUserFullDisplayName(user),
      inactive: user?.isActive === false,
      selectedOnly: true,
      unavailableLabel: allowedUserIds.has(value) ? "" : "no access",
      group: "Users",
      order: 10000,
      source: user,
    });
  });

  visibleTeams.forEach((team) => {
    optionsById.set(`team:${team.id}`, {
      value: `team:${team.id}`,
      label: `${team.name || "Unnamed Team"} (${team.eligibleMemberIds.length} users)`,
      group: "Teams",
      order: Number.isFinite(Number(team.order)) ? Number(team.order) : 9999,
      source: team,
    });
  });

  return Array.from(optionsById.values());
}

function renderTaskAssignedUsersField(collectionName, sortedAllowedUsers = null) {
  const formItemData = currentFormItemData();
  const container = document.getElementById("form-assigned_users-tags");
  if (!container) {return;}

  const allowedUsers = Array.isArray(sortedAllowedUsers)
    ? sortedAllowedUsers
    : getSortedUsers(getAssignableUsersWithPermission(collectionName));
  const allowedUserIds = allowedUsers.map((user) => user.id);
  const allowedUserMap = new Map(allowedUsers.map((user) => [user.id, user]));
  const allUsersMap = new Map((appData.users || [])
    .map((user) => [user.id, user]));

  formItemData.assigned_users = (formItemData.assigned_users || [])
    .filter((id) => allUsersMap.has(id));

  const visibleTeams = (appData.teams || [])
    .filter((team) => team?.visible !== false)
    .map((team) => {
      const eligibleMemberIds = (team.members || [])
        .filter((uid) => allowedUserIds.includes(uid));
      return { ...team, eligibleMemberIds };
    })
    .filter((team) => team.eligibleMemberIds.length > 0)
    .sort((a, b) => {
      const orderA = Number.isFinite(Number(a.order)) ? Number(a.order) : 999;
      const orderB = Number.isFinite(Number(b.order)) ? Number(b.order) : 999;
      if (orderA !== orderB) {return orderA - orderB;}
      return (a.name || "").localeCompare(b.name || "");
    });

  destroyRegisteredControlsIn(container);

  let assignedControl = null;
  let suppressChange = false;
  const selectedValues = formItemData.assigned_users || [];
  const assignedOptions = buildTaskAssignedUserOptions({
    allowedUsers,
    allUsersMap,
    selectedValues,
    visibleTeams,
  });
  assignedControl = createSelectControl({
    root: container,
    name: "assigned_users",
    value: selectedValues,
    options: assignedOptions,
    multiple: true,
    searchable: true,
    searchThreshold: PEOPLE_PICKER_SEARCH_THRESHOLD,
    placeholder: "+ Add assigned users",
    onChange: (values) => {
      if (suppressChange) {return;}
      const nextValues = Array.isArray(values) ? values : [];
      const teamSelection = nextValues.find((value) => String(value).startsWith("team:"));
      if (!teamSelection) {
        formItemData.assigned_users = nextValues;
        return;
      }

      const userValues = nextValues.filter((value) => !String(value).startsWith("team:"));
      suppressChange = true;
      assignedControl?.setValue(userValues);
      suppressChange = false;
      formItemData.assigned_users = userValues;

      const teamId = String(teamSelection).replace("team:", "");
      const team = visibleTeams.find((item) => item.id === teamId);
      const memberIds = team?.members || [];
      const eligibleMemberIds = team?.eligibleMemberIds || [];
      const memberListHtml = memberIds.length > 0
        ? `<ul class="mt-3 space-y-1 text-left">${memberIds.map((uid) => {
          const user = allUsersMap.get(uid) || allowedUserMap.get(uid);
          const canAdd = allowedUserMap.has(uid);
          const isAlreadyAssigned = userValues.includes(uid);
          const statusText = !canAdd
            ? '<span class="text-xs text-red-600">Will not add</span>'
            : isAlreadyAssigned
              ? '<span class="text-xs text-gray-400">Already assigned</span>'
              : "";
          return `<li class="flex items-center justify-between gap-3 rounded bg-gray-50 px-3 py-2 text-sm">
            <span>${sanitizeHTML(user?.name || uid)}</span>
            ${statusText}
          </li>`;
        }).join("")}</ul>`
        : '<p class="mt-3 text-sm text-gray-500">This team has no eligible members for this task.</p>';
      const hasIneligibleUsers = memberIds.some((uid) =>
        !allowedUserMap.has(uid));
      const permissionNote = hasIneligibleUsers
        ? '<p class="mt-3 text-sm text-gray-700">Users marked as will not add can&apos;t be added to this subtask due to insufficient permissions.</p>'
        : "";

      openConfirmationModal(
        `Add ${team?.name || "Team"} Members`,
        `<p class="text-sm text-gray-600">This will add the team&apos;s current members to <strong>Assigned Users</strong>.</p>${memberListHtml}${permissionNote}`,
        async () => {
          const nextAssignedUsers = new Set(formItemData.assigned_users || []);
          eligibleMemberIds.forEach((uid) => nextAssignedUsers.add(uid));
          formItemData.assigned_users = Array.from(nextAssignedUsers);
          suppressChange = true;
          assignedControl?.setValue(formItemData.assigned_users);
          suppressChange = false;
        },
        false,
        "Add Members",
      );
    },
  });
}

/**
 * Resolves task assignment scope from a parent item id.
 * @param {string} parentId - Parent item id.
 * @param {string | null} [storedParentType=null] Stored task parent type.
 * @returns {string | null} Collection permission scope.
 */
export function getTaskAssignmentScopeFromParent(
  parentId,
  storedParentType = null,
) {
  const parent = parentId ? findItem(parentId) : null;
  const parentType = storedParentType || (parent ? getItemType(parent.id) : null);
  return parentType ? getCollectionName(parentType) : null;
}
